"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { getStepNumber, ROUTE_PREFIXES, ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { usePathname } from "next/navigation"

import { BreadcrumbItem } from "./breadcrumb-item"
import * as styles from "./breadcrumb.styled"

type BreadcrumbProps = {
  className?: string
}

export const Breadcrumb = ({ className }: BreadcrumbProps) => {
  const { t } = useClientTranslation("global", { keyPrefix: "navbar" })
  const [state] = useActor(newSubscriptionFlowService)
  const hasMultiplePets = state.context.quote?.pets && state.context.quote.pets?.length > 1
  const isViewportBiggerThanMobile = useMediaQuery("(min-width: 1024px)")
  const pathname = usePathname()
  const completedStepsBarWidth = ((getStepNumber(pathname) - 1) / 14) * 100
  const isStateMatchesPetFlow = pathname.includes(ROUTE_PREFIXES.pet)
  const isStateMatchesUserFlow = pathname.includes(ROUTE_PREFIXES.user)
  const isStateMatchesHealthCoverage = pathname.includes(ROUTES.healthCoverage.path)
  const isStateMatchesPricing = pathname.includes(ROUTES.pricing.path)
  const isStateMatchesPrepackages = pathname === ROUTES.prepackages.path
  const isStateMatchesTribeQuote = pathname === ROUTES.tribeQuote.path
  const isStateMatchesParameters = pathname === ROUTES.healthCoverageParam.path
  const isStateMatchesMedicalHistory = pathname === ROUTES.medicalHistory.path
  const isStateMatchesUserInformations = pathname === ROUTES.userInformations.path
  const isStateMatchesUserAddress = pathname === ROUTES.userAddress.path
  const isStateMatchesUserBirthInformations = pathname === ROUTES.userBirthInformations.path
  const isStateMatchesRecap = pathname === ROUTES.recap.path
  const isStateMatchesQuote = isStateMatchesPrepackages || isStateMatchesTribeQuote || isStateMatchesParameters || isStateMatchesMedicalHistory || isStateMatchesHealthCoverage || isStateMatchesPricing
  const isStateMatchesUserInformationsPart2 = isStateMatchesUserInformations || isStateMatchesUserAddress || isStateMatchesUserBirthInformations

  const renderInitialSteps = () => (
    <>
      <BreadcrumbItem
        variant={isStateMatchesPetFlow ? "default" : "disabled"}
        stepNumber={1}
        isActive={isStateMatchesPetFlow}
      >
        {hasMultiplePets ? t("multi-pets") : t("pet-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesUserFlow ? "default" : "disabled"}
        stepNumber={2}
        isActive={isStateMatchesUserFlow}
      >
        {t("user-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={
          isStateMatchesPrepackages ||
          isStateMatchesTribeQuote ||
          isStateMatchesParameters ||
          isStateMatchesMedicalHistory ||
          (isStateMatchesQuote && hasMultiplePets)
            ? "default"
            : "disabled"
        }
        stepNumber={4}
        isActive={
          isStateMatchesPrepackages ||
          isStateMatchesTribeQuote ||
          isStateMatchesParameters ||
          isStateMatchesMedicalHistory ||
          (isStateMatchesQuote && hasMultiplePets)
        }
      >
        {t("coverage")}
      </BreadcrumbItem>
    </>
  )

  const renderQuoteSteps = () => (
    <>
      <BreadcrumbItem
        variant={isStateMatchesQuote ? "default" : "disabled"}
        stepNumber={1}
        isActive={isStateMatchesQuote}
      >
        {t("quote")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesUserInformationsPart2 ? "default" : "disabled"}
        stepNumber={2}
        isActive={isStateMatchesUserInformationsPart2}
      >
        {t("user-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesRecap ? "default" : "disabled"}
        stepNumber={4}
        isActive={isStateMatchesRecap}
      >
        {t("recap")}
      </BreadcrumbItem>
    </>
  )

  return (
    <>
      {isViewportBiggerThanMobile ? (
        <>
          <div className={styles.progressBarContainerTop}>
            <div className={styles.progressBarTop}>
              <div
                className={styles.completedStepsBarTop}
                style={{ width: `${completedStepsBarWidth}%`, transition: "all 0.3s ease" }}
              />
              <div className={styles.activeStepTop}>
                <div className={styles.activeStepIndicatorTop} />
              </div>
              <div className={styles.uncompletedStepsBarTop} />
            </div>
            <nav className={styles.root(className)}>
              {isStateMatchesPetFlow ||
              isStateMatchesUserFlow ||
              isStateMatchesPrepackages ||
              isStateMatchesTribeQuote ||
              isStateMatchesParameters ||
              isStateMatchesMedicalHistory ||
              (hasMultiplePets && isStateMatchesQuote)
                ? renderInitialSteps()
                : renderQuoteSteps()}
            </nav>
          </div>
        </>
      ) : null}
    </>
  )
}
